import React, { useState, useEffect, useRef } from "react";
import {
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  doc,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import Modal from "react-modal";
import * as XLSX from "xlsx";
import ClientsTable from "./ClientsTable";
import Nav from "./Nav";
import Sidebar from "./Sidebar";
import moment from "moment";
import Spinner from "./Spinner";
import { useNavContext } from "../context/NavContext";
import { useLocation } from "react-router-dom";

moment.locale("es");
Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Roboto, sans-serif",
    "::WebkitScrollbar": { width: "0px", background: "transparent" },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  label: {
    display: "block",
    width: "160px",
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  input: {
    width: "100%",
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 101 },
};

const stepContainerStyle = {
  display: "flex",
  justifyContent: "left",
  marginLeft: "100px",
  gap: "50px",
  marginBottom: "20px",
  cursor: "pointer",
};

const stepStyle = (isActive) => ({
  padding: "10px 15px",
  borderBottom: isActive ? "4px solid #007bff" : "4px solid transparent",
  transition: "border-bottom 0.3s ease",
  fontWeight: isActive ? "bold" : "normal",
});

const addrScore = (c) => {
  const calle = c.calle || c.domicilio || c.direccion?.calle || "";
  const numeroExt = c.numeroExt || c.direccion?.numeroExt || "";
  const colonia = c.colonia || c.direccion?.colonia || "";
  const ciudad = c.ciudad || c.direccion?.ciudad || "";
  const estado = c.estado || c.direccion?.estado || "";
  const cp = c.codigoPostal || c.direccion?.codigoPostal || "";
  return (
    (calle ? 1 : 0) +
    (numeroExt ? 1 : 0) +
    (colonia ? 1 : 0) +
    (ciudad ? 1 : 0) +
    (estado ? 1 : 0) +
    (cp ? 1 : 0)
  );
};

const ListaClientes = () => {
  const location = useLocation();
  const [clientes, setClientes] = useState([]);
  const [formData, setFormData] = useState({
    empresa: "",
    rfc: "",
    regimenFiscal: "",
    moneda: "",
    telefono: "",
    correo: "",
    imagenURL: null,
    calle: "",
    numeroExt: "",
    numeroInt: "",
    colonia: "",
    codigoPostal: "",
    ciudad: "",
    estado: "",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [editingClienteId, setEditingClienteId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [showNuevoButton] = useState(true);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  const regimenFiscalTranslationMap = {
    601: "601 - General de Ley Personas Morales",
    612: "612 - Personas Físicas con Actividades Empresariales y Profesionales",
    616: "616 - Sin obligaciones fiscales",
    626: "626 - Régimen Simplificado de Confianza (RESICO)",
  };
  const translateRegimenFiscal = (value) => {
    const code = String(value || "").split(" ")[0];
    return regimenFiscalTranslationMap[code] || value;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      Swal.fire({
        title: "Error",
        text: "No se seleccionó ningún archivo.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        if (!workbook.SheetNames.length)
          throw new Error("El archivo no contiene hojas válidas.");
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet) throw new Error("No se pudo leer la hoja seleccionada.");
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!jsonData.length)
          throw new Error("El archivo no contiene datos válidos.");

        const clientesCollectionRef = collection(db, "clientes");
        let nuevosClientes = 0;
        const updatePromises = [];

        for (const row of jsonData) {
          const cliente = {
            empresa: row["Company Name"] || "Sin nombre",
            rfc: row["Tax Registration Number"] || "Sin RFC",
            regimenFiscal: translateRegimenFiscal(row["Tax Regime"] || ""),
            telefono: row["Phone"] || "",
            correo: row["EmailID"] || "",
            direccion: {
              calle: row["Billing Attention"] || "",
              ciudad: row["Billing City"] || "",
              estado: row["Billing State"] || "",
              pais: row["Billing Country"] || "",
              codigoPostal: row["Billing Code"] || "",
            },
            status: row["Status"] || "Activo",
            datosCompletos: row,
          };

          const clienteQuery = query(
            clientesCollectionRef,
            where("rfc", "==", cliente.rfc)
          );
          const querySnapshot = await getDocs(clienteQuery);

          if (querySnapshot.empty) {
            await addDoc(clientesCollectionRef, cliente);
            nuevosClientes++;
          } else {
            querySnapshot.forEach((docSnapshot) => {
              const docRef = doc(db, "clientes", docSnapshot.id);
              updatePromises.push(updateDoc(docRef, cliente));
            });
          }
        }

        const updateResults = await Promise.all(updatePromises);
        const actualizaciones = updateResults.length;

        Swal.fire({
          title: "Importación exitosa",
          text: `${jsonData.length} cliente(s) procesado(s): ${nuevosClientes} agregado(s), ${actualizaciones} actualizado(s).`,
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (error) {
        console.error("Error al procesar el archivo:", error);
        Swal.fire({
          title: "Error",
          text: `Error al procesar el archivo: ${error.message}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    reader.onerror = () =>
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error al leer el archivo. Inténtalo nuevamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowOptionsMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleImportClick = () => fileInputRef.current.click();

  const searchClientes = (term) => {
    const lower = term.toLowerCase();
    return clientes.filter((c) => {
      const empresa = c.empresa || "";
      const rfc = c.rfc || "";
      const regimen = c.regimenFiscal || "";
      return (
        empresa.toLowerCase().includes(lower) ||
        rfc.toLowerCase().includes(lower) ||
        regimen.toLowerCase().includes(lower)
      );
    });
  };

  // === LECTURA Y NORMALIZACIÓN: deduplicar por RFC/empresa, escoger mejor dirección ===
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "clientes"), (snapshot) => {
      const docs = snapshot.docs.map((d) => ({ ...d.data(), __docId: d.id }));

      const elegirMejor = new Map(); // clave => cliente con mejor dirección
      for (const c of docs) {
        const clave = (c.rfc || c.empresa || c.__docId || "").toLowerCase();
        const actual = elegirMejor.get(clave);
        if (!actual) {
          elegirMejor.set(clave, c);
        } else {
          const mejor = addrScore(c) > addrScore(actual) ? c : actual;
          elegirMejor.set(clave, mejor);
        }
      }

      setClientes(Array.from(elegirMejor.values()));
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!(formData.empresa || "").trim()) {
      Swal.fire({ icon: "error", title: "Falta el nombre de la empresa" });
      return;
    }

    const payload = {
      empresa: formData.empresa || "",
      rfc: formData.rfc || "",
      regimenFiscal: formData.regimenFiscal || "",
      moneda: formData.moneda || "",
      telefono: formData.telefono || "",
      correo: formData.correo || "",
      imagenURL: formData.imagenURL ?? null,
      // también guardamos en raíz (compat. con docs antiguos)
      calle: formData.calle || "",
      numeroExt: formData.numeroExt || "",
      numeroInt: formData.numeroInt || "",
      colonia: formData.colonia || "",
      codigoPostal: formData.codigoPostal || "",
      ciudad: formData.ciudad || "",
      estado: formData.estado || "",
      // y en objeto anidado
      direccion: {
        calle: formData.calle || "",
        numeroExt: formData.numeroExt || "",
        numeroInt: formData.numeroInt || "",
        colonia: formData.colonia || "",
        codigoPostal: formData.codigoPostal || "",
        ciudad: formData.ciudad || "",
        estado: formData.estado || "",
      },
    };

    try {
      setSaving(true);

      if (editingClienteId) {
        // resolver __docId real
        let targetId = editingClienteId;
        const localMatch = clientes.find(
          (c) => c.__docId === targetId || c.id === targetId
        );
        if (localMatch && localMatch.__docId) targetId = localMatch.__docId;

        const refDoc = doc(db, "clientes", targetId);
        const snap = await getDoc(refDoc);
        if (!snap.exists()) {
          throw new Error(
            `El documento a actualizar no existe (id: ${targetId}). Vuelve a abrir el modal desde la tabla.`
          );
        }

        await updateDoc(refDoc, payload);
        Swal.fire({
          icon: "success",
          title: "¡Cliente actualizado!",
          timer: 1000,
          showConfirmButton: false,
        });
      } else {
        await addDoc(collection(db, "clientes"), payload);
        Swal.fire({
          icon: "success",
          title: "¡Cliente registrado!",
          timer: 1000,
          showConfirmButton: false,
        });
      }

      // limpiar y cerrar
      setFormData({
        empresa: "",
        rfc: "",
        regimenFiscal: "",
        moneda: "",
        telefono: "",
        correo: "",
        imagenURL: null,
        calle: "",
        numeroExt: "",
        numeroInt: "",
        colonia: "",
        codigoPostal: "",
        ciudad: "",
        estado: "",
      });
      setEditingClienteId(null);
      setStep(1);
      setModalIsOpen(false);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditClient = (id) => {
    const clientToEdit =
      clientes.find((c) => c.__docId === id) ||
      clientes.find((c) => c.id === id);

    if (!clientToEdit) {
      Swal.fire({ icon: "error", title: "Cliente no encontrado" });
      return;
    }

    setFormData({
      empresa: clientToEdit.empresa || "",
      rfc: clientToEdit.rfc || "",
      regimenFiscal:
        (clientToEdit.regimenFiscal &&
          String(clientToEdit.regimenFiscal).split(" ")[0]) ||
        "",
      moneda:
        clientToEdit.moneda ||
        clientToEdit.datosCompletos?.["Currency Code"] ||
        "",
      telefono: clientToEdit.telefono || "",
      correo: clientToEdit.correo || "",
      imagenURL: clientToEdit.imagenURL || null,

      // Dirección: prioriza campos raíz; luego direccion.*; luego import
      calle:
        clientToEdit.calle ||
        clientToEdit.domicilio ||
        clientToEdit.direccion?.calle ||
        clientToEdit.datosCompletos?.["Billing Address"] ||
        "",
      numeroExt:
        clientToEdit.numeroExt || clientToEdit.direccion?.numeroExt || "",
      numeroInt:
        clientToEdit.numeroInt || clientToEdit.direccion?.numeroInt || "",
      colonia: clientToEdit.colonia || clientToEdit.direccion?.colonia || "",
      codigoPostal:
        clientToEdit.codigoPostal || clientToEdit.direccion?.codigoPostal || "",
      ciudad:
        clientToEdit.ciudad ||
        clientToEdit.direccion?.ciudad ||
        clientToEdit.datosCompletos?.["Billing City"] ||
        "",
      estado: clientToEdit.estado || clientToEdit.direccion?.estado || "",
    });

    setEditingClienteId(clientToEdit.__docId || id);
    setStep(1);
    setModalIsOpen(true);
  };

  const handleDeleteClient = async (id) => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "¡No podrás revertir esto!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const match =
            clientes.find((c) => c.__docId === id) ||
            clientes.find((c) => c.id === id);
          const realId = match?.__docId || id;
          await deleteDoc(doc(db, "clientes", realId));
          Swal.fire({
            icon: "success",
            title: "¡Cliente eliminado con éxito!",
            showConfirmButton: false,
            timer: 1000,
          });
        } catch (error) {
          console.error("Error al eliminar cliente:", error);
        }
      }
    });
  };

  const resetFormData = () => {
    setFormData({
      empresa: "",
      rfc: "",
      regimenFiscal: "",
      moneda: "",
      telefono: "",
      correo: "",
      imagenURL: null,
      calle: "",
      numeroExt: "",
      numeroInt: "",
      colonia: "",
      codigoPostal: "",
      ciudad: "",
      estado: "",
    });
  };

  const handleOpenModal = () => {
    resetFormData();
    setEditingClienteId(null);
    setModalIsOpen(true);
  };

  return (
    <>
      <Nav
        handleSearch={searchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>

          {/* 🔎 Buscador a la IZQUIERDA, igual que Proveedores */}
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar clientes"
              value={searchTerm ?? ""}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="task-filters-right">
          <div
            className="view-buttons-container"
            style={{ position: "relative" }}
          >
            <button className="view-button">
              <i className="bi bi-three-dots-vertical"></i>
            </button>
            <button className="exportar-button">
              <i className="bi bi-cloud-arrow-down"></i> Exportar
            </button>
            {showNuevoButton && (
              <button className="new-task-button" onClick={handleOpenModal}>
                + Nuevo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="cotizaciones-table">
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={() => setModalIsOpen(false)}
          style={customStyles}
        >
          <div>
            <button
              className="cerrar-button"
              onClick={() => setModalIsOpen(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <Nav
            handleSearch={searchTerm}
            proximasAVencer={proximasAVencer}
            proximosEventos={proximosEventos}
          />

          <Sidebar />

          {step === 1 && (
            <>
              <h2 style={{ textAlign: "left", marginLeft: "100px" }}>
                {editingClienteId ? "Editar Cliente" : "Agregar nuevo cliente"}
              </h2>
              <div style={stepContainerStyle}>
                <div style={stepStyle(step === 1)} onClick={() => setStep(1)}>
                  Info
                </div>
                <div style={stepStyle(step === 2)} onClick={() => setStep(2)}>
                  Dirección
                </div>
              </div>

              <form onSubmit={handleSubmit} className="client-form">
                <div className="form-row">
                  <label className="required" style={customStyles.label}>
                    Razón Social*
                  </label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="empresa"
                    placeholder="Nombre de la Empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>RFC</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="rfc"
                    placeholder="RFC"
                    value={formData.rfc}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Régimen Fiscal</label>
                  <select
                    style={customStyles.select}
                    id="regimen"
                    name="regimenFiscal"
                    value={formData.regimenFiscal}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Seleccione un régimen fiscal
                    </option>
                    <option value="601">
                      601 - General de Ley Personas Morales
                    </option>
                    <option value="612">
                      612 - Personas Físicas con Actividades Empresariales y
                      Profesionales
                    </option>
                    <option value="616">616 - Sin obligaciones fiscales</option>
                    <option value="626">
                      626 - Régimen Simplificado de Confianza (RESICO)
                    </option>
                  </select>

                  <label style={customStyles.label}>Moneda</label>
                  <select
                    style={customStyles.select}
                    id="moneda"
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Seleccione una moneda
                    </option>
                    <option value="MXN">MXN</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Teléfono</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="telefono"
                    placeholder="Teléfono"
                    value={formData.telefono}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>Email</label>
                  <input
                    style={customStyles.input}
                    type="email"
                    name="correo"
                    placeholder="Correo Electrónico"
                    value={formData.correo}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Imagen</label>
                  <input
                    type="file"
                    name="imagen"
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      display: "block",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h2 style={{ textAlign: "left", marginLeft: "100px" }}>
                {editingClienteId
                  ? "Editar dirección del cliente"
                  : "Dirección del cliente"}
              </h2>
              <div style={stepContainerStyle}>
                <div style={stepStyle(step === 1)} onClick={() => setStep(1)}>
                  Info
                </div>
                <div style={stepStyle(step === 2)} onClick={() => setStep(2)}>
                  Dirección
                </div>
              </div>

              <form onSubmit={handleSubmit} className="client-form">
                <div className="form-row">
                  <label style={customStyles.label}>Calle</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="calle"
                    placeholder="Calle"
                    value={formData.calle}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>No. Exterior</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="numeroExt"
                    placeholder="No. Exterior"
                    value={formData.numeroExt}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>No. Interior</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="numeroInt"
                    placeholder="No. Interior"
                    value={formData.numeroInt}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>Colonia</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="colonia"
                    placeholder="Colonia"
                    value={formData.colonia}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Código Postal</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="codigoPostal"
                    placeholder="Código Postal"
                    value={formData.codigoPostal}
                    onChange={handleChange}
                  />
                  <label style={customStyles.label}>Ciudad</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="ciudad"
                    placeholder="Ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-row">
                  <label style={customStyles.label}>Estado</label>
                  <input
                    style={customStyles.input}
                    type="text"
                    name="estado"
                    placeholder="Estado"
                    value={formData.estado}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-toolbar">
                  <div className="leftBtn">
                    <button
                      id="guardar-enviar"
                      className="btn btn-primary"
                      type="submit"
                      disabled={saving}
                    >
                      {saving
                        ? "Guardando..."
                        : editingClienteId
                        ? "Actualizar"
                        : "Guardar y enviar"}
                    </button>
                    <button
                      id="cancelar"
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => setModalIsOpen(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </Modal>

        {isLoading ? (
          <Spinner />
        ) : searchTerm !== "" && searchClientes(searchTerm).length === 0 ? (
          <div className="no-resultados">
            <div
              className="mensaje-sin-resultados"
              style={{ marginTop: "30px" }}
            >
              <p style={{ fontSize: "14px" }}>
                No existe ningún cliente que coincida con esta búsqueda.
              </p>
            </div>
            <div className="imagen-sin-resultados">
              <img
                src="/img/noSearch.svg"
                alt="Sin resultados"
                className="sin-resultados-imagen"
              />
            </div>
          </div>
        ) : (
          <ClientsTable
            clientes={searchTerm ? searchClientes(searchTerm) : clientes}
            onEditClient={handleEditClient}
            onDeleteClient={handleDeleteClient}
          />
        )}
      </div>

      {showOptionsMenu && (
        <div className="dropdown-options" ref={dropdownRef}>
          <p onClick={handleImportClick}>
            <i
              className="bi bi-box-arrow-in-down"
              style={{ marginRight: "10px" }}
            ></i>
            Importar
          </p>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
          <p>
            <i
              className="bi bi-box-arrow-up"
              style={{ marginRight: "10px" }}
            ></i>
            Exportar
          </p>
        </div>
      )}
    </>
  );
};

export default ListaClientes;
